import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Send } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/site/Sections";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { submitIntake, submitContact } from "@/lib/api";

const HOUSING_OPTIONS = [
  "Currently homeless",
  "In a shelter",
  "Transitioning from incarceration",
  "In recovery program",
  "Veteran housing / VA",
  "Temporary / staying with others",
  "At risk of losing housing",
  "Other",
];

const field = "bg-input border-border text-foreground placeholder:text-muted-foreground focus-visible:ring-primary";

const emptyIntake = {
  full_name: "", email: "", phone: "", age: "", housing_situation: "",
  dependents: "0", reason: "", medical_concerns: "", notes: "",
};

export const IntakeForm = () => {
  const [form, setForm] = useState(emptyIntake);
  const [moveIn, setMoveIn] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.age || !form.housing_situation || !form.reason) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await submitIntake({
        ...form,
        age: parseInt(form.age, 10),
        dependents: parseInt(form.dependents || "0", 10),
        move_in_date: moveIn ? format(moveIn, "yyyy-MM-dd") : null,
      });
      toast.success("Application received. Our team will reach out to you soon.");
      setForm(emptyIntake);
      setMoveIn(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="apply" data-testid="intake-section" className="bg-background py-28 lg:py-40">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <p className="text-xs font-sans uppercase tracking-[0.3em] font-semibold text-primary mb-4">
              Intake application
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-foreground leading-[0.95]">
              Take the first step toward a fresh start.
            </h2>
            <p className="mt-6 font-sans text-foreground/70 leading-relaxed">
              Your information is kept confidential and reviewed with care and compassion by our team.
              Fields marked with <span className="text-primary">*</span> are required.
            </p>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-8">
            <form onSubmit={submit} data-testid="intake-form" className="rounded-2xl bg-card border border-border p-8 lg:p-10 grid sm:grid-cols-2 gap-6">
              <div className="sm:col-span-2">
                <Label className="text-foreground">Full name *</Label>
                <Input data-testid="intake-full-name" value={form.full_name} onChange={set("full_name")} className={`mt-2 ${field}`} placeholder="Jane Doe" />
              </div>
              <div>
                <Label className="text-foreground">Email *</Label>
                <Input data-testid="intake-email" type="email" value={form.email} onChange={set("email")} className={`mt-2 ${field}`} placeholder="jane@email.com" />
              </div>
              <div>
                <Label className="text-foreground">Phone *</Label>
                <Input data-testid="intake-phone" value={form.phone} onChange={set("phone")} className={`mt-2 ${field}`} placeholder="(555) 000-0000" />
              </div>
              <div>
                <Label className="text-foreground">Age *</Label>
                <Input data-testid="intake-age" type="number" min="18" value={form.age} onChange={set("age")} className={`mt-2 ${field}`} placeholder="34" />
              </div>
              <div>
                <Label className="text-foreground">Number of dependents</Label>
                <Input data-testid="intake-dependents" type="number" min="0" value={form.dependents} onChange={set("dependents")} className={`mt-2 ${field}`} />
              </div>
              <div>
                <Label className="text-foreground">Current housing situation *</Label>
                <Select value={form.housing_situation} onValueChange={(v) => setForm((f) => ({ ...f, housing_situation: v }))}>
                  <SelectTrigger data-testid="intake-housing-trigger" className={`mt-2 ${field}`}>
                    <SelectValue placeholder="Select one" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {HOUSING_OPTIONS.map((o) => (
                      <SelectItem key={o} value={o} data-testid={`housing-opt-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-foreground">Preferred move-in date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="intake-movein-trigger"
                      className={`mt-2 w-full justify-start font-normal ${field} ${!moveIn && "text-muted-foreground"}`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {moveIn ? format(moveIn, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                    <Calendar mode="single" selected={moveIn} onSelect={setMoveIn} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-foreground">Reason for seeking housing *</Label>
                <Textarea data-testid="intake-reason" value={form.reason} onChange={set("reason")} className={`mt-2 ${field}`} rows={3} placeholder="Tell us a little about your current situation." />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-foreground">Medical concerns</Label>
                <Textarea data-testid="intake-medical" value={form.medical_concerns} onChange={set("medical_concerns")} className={`mt-2 ${field}`} rows={2} placeholder="Any medical conditions or accessibility needs we should know about." />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-foreground">Additional notes</Label>
                <Textarea data-testid="intake-notes" value={form.notes} onChange={set("notes")} className={`mt-2 ${field}`} rows={2} />
              </div>
              <div className="sm:col-span-2">
                <Button data-testid="intake-submit-btn" type="submit" disabled={loading} className="rounded-full bg-primary text-primary-foreground hover:bg-foreground hover:text-background transition-colors duration-300 h-12 px-8 text-base">
                  {loading ? "Submitting..." : "Submit Application"}
                  <Send className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const emptyContact = { name: "", email: "", phone: "", message: "" };

export const ContactForm = () => {
  const [form, setForm] = useState(emptyContact);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Message sent. We'll be in touch soon.");
      setForm(emptyContact);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-card py-28 lg:py-40">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <p className="text-xs font-sans uppercase tracking-[0.3em] font-semibold text-primary mb-4">
            Get in touch
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-foreground leading-[0.95]">
            Have questions? We&apos;re here to help.
          </h2>
          <div className="mt-8 space-y-4 font-sans text-foreground/75">
            <p><span className="text-primary">Email</span><br />info@freshstartservices.net</p>
            <p><span className="text-primary">Phone</span><br />708-393-1243</p>
            <p><span className="text-primary">Hours</span><br />Monday – Friday, 9am – 6pm</p>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl bg-background border border-border p-8 lg:p-10 grid gap-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <Label className="text-foreground">Name *</Label>
                <Input data-testid="contact-name" value={form.name} onChange={set("name")} className={`mt-2 ${field}`} />
              </div>
              <div>
                <Label className="text-foreground">Email *</Label>
                <Input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} className={`mt-2 ${field}`} />
              </div>
            </div>
            <div>
              <Label className="text-foreground">Phone</Label>
              <Input data-testid="contact-phone" value={form.phone} onChange={set("phone")} className={`mt-2 ${field}`} />
            </div>
            <div>
              <Label className="text-foreground">Message *</Label>
              <Textarea data-testid="contact-message" value={form.message} onChange={set("message")} className={`mt-2 ${field}`} rows={4} />
            </div>
            <Button data-testid="contact-submit-btn" type="submit" disabled={loading} className="rounded-full bg-primary text-primary-foreground hover:bg-foreground hover:text-background transition-colors duration-300 h-12 px-8 text-base w-fit">
              {loading ? "Sending..." : "Send Message"}
            </Button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
